'use client'

// The laundrette (Sam, 3 Nov: "There is a laundrette model I have somewhere ... I want this adding to the world"): the
// Laundry pack's own building, its machines, shelves, baskets and the LAUNDRY sign over the door, standing on its own
// on the north side of town, past the monorail. Laid out in the blend at world coordinates (collection Laundrette, one
// handle, laundrette_root), so it renders at identity. The old laundrette front on the brick block stays where it is;
// this is where it moved to ("shold become the place his old one moved to"), and his spot for watching the drums and
// the visitors' actions are here.
//
// As the pack has it, 285 meshes on 44 materials. scripts/blender/laundrette.py makes it three meshes (solid, the cut
// out clothes, the glass) and its textures one atlas, the monorail's way: each face's second UV set is the corner of
// its cell, and a surface that repeats its texture repeats it inside the cell. The atlas's alpha is the cut on the
// clothes and the glow everywhere else (0 is lit by itself: the sign's letters and the strip lights' tubes, day and
// night, as the monorail's lights are). Three draws and one shadow draw; nearest-filtered and dithered like the rest.
//
// The front door and the office door are drawn on their own, standing on their hinges, and swing open when the camera
// or he comes near (Sam: "internal door dosn't open - can it auto open when we get close to it?"), and shut again
// after. Their doorways are gaps in the walls either way (lib/world/laundrette.js), so a door only has to be open by
// the time you reach it. Three draws more. What visitors have done there in the last day stands in it
// (LaundretteThings.jsx), asked of the town only while the camera is near, and the washers that are going round have
// washing tumbling behind their doors (Sam: "a graphic of washing in the washing machine that spins with the active
// machines"), which ones worked out from the clock as the town works it out (lib/world/laundretteMachines.js).

import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { texture, uv, fract, vec2, float } from 'three/tsl'
import { useModel, useKTX2Texture } from '../hooks/useModel'
import { ditherMaterial } from '../effects/ditherRegistry'
import { birdPose } from '../../../lib/world/birdPose'
import { LAUNDRETTE_DOORS, LAUNDRETTE_ROOT } from '../../../lib/world/laundrette'
import { LAUNDRETTE_THINGS, SETS, showsFor } from '../../../lib/world/laundretteThings'
import { runningAt } from '../../../lib/world/laundretteMachines'
import { LaundretteThings } from './LaundretteThings'

const MODEL = '/models/laundrette.glb'
const ATLAS = '/images/laundrette_atlas.ktx2'
// the atlas: 256 px cells, 8 across and 6 down (laundrette.py writes it)
const SIZE = [2048, 1536]
const CELL = 256
// a door opens with the camera or him this near the middle of its doorway (metres), and takes this long to swing
// (seconds): at a walk, 3 m is half a second away, so it is open as you get there
const OPEN_AT = 3
const SWING = 0.5
// the town is asked what has been done here only with the camera this near (metres), once a minute: a few dozen
// bytes, and nobody across town asks at all
const ASK_NEAR = 90
const ASK_EVERY = 60_000
// on a local page, ?laundrette=all shows every action's thing at once, for looking them over
const SHOW_ALL = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('laundrette') === 'all'

// Same conversion the wallet and the price panel do: the world is reached over the socket, its files over http on
// the same host.
function simBase() {
  const ws = process.env.NEXT_PUBLIC_SIM_WS_URL || ''
  return ws.replace(/^wss:\/\//, 'https://').replace(/^ws:\/\//, 'http://').replace(/\/$/, '')
}

export function Laundrette() {
  const { scene } = useModel(MODEL)
  const atlas = useKTX2Texture(ATLAS)
  const doors = useMemo(() => LAUNDRETTE_DOORS.map((d) => ({ ...d, meshes: d.nodes.map((n) => scene.getObjectByName(n)).filter(Boolean), t: 0 }))
    .filter((d) => d.meshes.length), [scene])

  // what people have done here, as the town keeps it (ids and when), and the clock it is read against
  const [done, setDone] = useState([])
  const [now, setNow] = useState(() => Date.now())
  const near = useRef(false)
  useEffect(() => {
    let dead = false
    let last = 0
    const look = () => {
      setNow(Date.now())
      if (!near.current || Date.now() - last < ASK_EVERY) return
      last = Date.now()
      fetch(`${simBase()}/api/traces/at?place=laundry`, { cache: 'no-store' })
        .then((r) => r.json())
        .then((d) => { if (!dead && Array.isArray(d?.things)) setDone(d.things) })
        .catch(() => { /* the laundrette stays as it was */ })
    }
    const t = setInterval(look, 5000)
    return () => { dead = true; clearInterval(t) }
  }, [])
  const live = useMemo(() => (SHOW_ALL ? Object.keys(LAUNDRETTE_THINGS)
    : [...new Set(done.filter((d) => LAUNDRETTE_THINGS[d.id] && now - d.at < showsFor(d.id)).map((d) => d.id))]), [done, now])
  const propped = live.includes(SETS.door)
  // the washers going round now, read off the clock as the town reads it, so the one he stands at is one going
  const running = useMemo(() => runningAt(now), [now])

  useFrame(({ camera }, dt) => {
    near.current = Math.hypot(camera.position.x - LAUNDRETTE_ROOT.x, camera.position.z - LAUNDRETTE_ROOT.z) < ASK_NEAR
    for (const d of doors) {
      const open = (d.name === 'front' && propped)
        || Math.hypot(camera.position.x - d.x, camera.position.z - d.z) < OPEN_AT
        || Math.hypot(birdPose.x - d.x, birdPose.z - d.z) < OPEN_AT
      const t = THREE.MathUtils.clamp(d.t + (open ? dt : -dt) / SWING, 0, 1)
      if (t === d.t) continue
      d.t = t
      for (const m of d.meshes) m.rotation.y = d.open * t * t * (3 - 2 * t)
    }
  })

  // made here, not in an effect, so the first frame draws the final materials
  useMemo(() => {
    atlas.magFilter = THREE.NearestFilter
    atlas.minFilter = THREE.NearestFilter
    atlas.needsUpdate = true
    // a texel's centre inside the face's cell: kept half a texel in, so a repeating floor never catches the edge of
    // the cell beside it
    const look = () => texture(atlas, uv(1).add(fract(uv()).mul(CELL - 1).add(0.5).div(vec2(SIZE[0], SIZE[1]))))

    const solid = new THREE.MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0 })
    const s = look()
    solid.colorNode = s.rgb
    solid.emissiveNode = s.rgb.mul(float(1).sub(s.a))

    const cut = new THREE.MeshStandardNodeMaterial({ roughness: 0.95, metalness: 0, side: THREE.DoubleSide, alphaTest: 0.5 })
    const c = look()
    cut.colorNode = c.rgb
    cut.opacityNode = c.a

    const glass = new THREE.MeshStandardNodeMaterial({
      color: '#a8c3cb', roughness: 0.2, metalness: 0, transparent: true, opacity: 0.3, depthWrite: false, side: THREE.DoubleSide,
    })

    const made = { laundrette_solid: solid, laundrette_cut: cut, laundrette_glass: glass }
    for (const m of Object.values(made)) ditherMaterial(m)
    scene.traverse((o) => {
      const door = o.name.startsWith('laundrette_door_')
      const m = made[o.name] || (door ? (o.name.endsWith('_glass') ? glass : solid) : null)
      if (!o.isMesh || !m) return
      o.material = m
      // only the building casts: what is inside it is in its shadow already, and the clothes and glass are too thin
      o.castShadow = o.name === 'laundrette_solid'
      o.receiveShadow = m !== glass
    })
  }, [scene, atlas])

  // Baked transform: render at identity.
  return (
    <>
      <primitive object={scene} />
      <LaundretteThings live={live} running={running} />
    </>
  )
}

export default Laundrette
